import { useEffect, useState } from "react";
import {
  Button,
  FieldError,
  Input,
  Label,
  Modal,
  ModalFooter,
  fieldErrorId,
} from "@/components/ui";

type ChangeRoomModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Next room label, or ``null`` when clearing the room. */
  onConfirm: (nextRoomName: string | null) => void;
  title: string;
  currentRoomLabel: string;
  newRoomLabel: string;
  currentRoomName: string | null;
  noRoomLabel: string;
  placeholder: string;
  unchangedHint: string;
  confirmText: string;
  removeText: string;
  cancelText: string;
  closeLabel: string;
  isLoading?: boolean;
};

export function ChangeRoomModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  currentRoomLabel,
  newRoomLabel,
  currentRoomName,
  noRoomLabel,
  placeholder,
  unchangedHint,
  confirmText,
  removeText,
  cancelText,
  closeLabel,
  isLoading = false,
}: ChangeRoomModalProps) {
  const [draft, setDraft] = useState(currentRoomName ?? "");
  const [error, setError] = useState<string | null>(null);
  const hasCurrentRoom = Boolean(currentRoomName?.trim());
  const roomInputId = "change-room-name";
  const roomErrorId = fieldErrorId(roomInputId);

  useEffect(() => {
    if (isOpen) {
      setDraft(currentRoomName ?? "");
      setError(null);
    }
  }, [isOpen, currentRoomName]);

  const submit = () => {
    const next = draft.trim() || null;
    const current = currentRoomName?.trim() || null;
    if (next === current) {
      setError(unchangedHint);
      return;
    }
    setError(null);
    onConfirm(next);
  };

  const removeRoom = () => {
    setError(null);
    onConfirm(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeLabel={closeLabel}
      title={title}
      size="sm"
      priority="high"
      showCloseButton={!isLoading}
      closeOnEscape={!isLoading}
      dismissOnBackdrop={!isLoading}
      footer={
        <ModalFooter
          className={
            hasCurrentRoom ? "sm:justify-between" : undefined
          }
        >
          {hasCurrentRoom ? (
            <Button
              variant="outline"
              className="border-error-200 text-error-700 hover:border-error-300 hover:bg-error-50 hover:text-error-800"
              onClick={removeRoom}
              disabled={isLoading}
            >
              {removeText}
            </Button>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {cancelText}
            </Button>
            <Button onClick={submit} isLoading={isLoading}>
              {confirmText}
            </Button>
          </div>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="text-[12px] font-medium text-surface-500">
            {currentRoomLabel}
          </p>
          <p className="mt-1 text-[14px] font-semibold text-surface-900">
            {currentRoomName?.trim() || noRoomLabel}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={roomInputId}>{newRoomLabel}</Label>
          <Input
            id={roomInputId}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submit();
              }
            }}
            placeholder={placeholder}
            disabled={isLoading}
            autoFocus
            aria-invalid={error != null}
            aria-describedby={error != null ? roomErrorId : undefined}
          />
          {error ? <FieldError id={roomErrorId}>{error}</FieldError> : null}
        </div>
      </div>
    </Modal>
  );
}
